import { useCallback, useEffect, useState } from 'react';
import { ServerSetting, getServerSettings } from '../../services/adminServerSettings';
import AdminPage from './AdminPage';
import { errorMessage } from './adminErrors';
import SettingRow from './SettingRow';
import { UnitsPageKeys } from './Units/unitsPolicy';

const DESCRIPTIONS: Record<string, string> = {
  MinimumEngineVersion:
    'The lowest engine version a game may have. A game whose active version is at a lower engine version is ' +
    'upgraded to the newest engine before its next chat request, and can’t be built until then. A positive integer.',
  TuningEditorEnabled: 'Shows the tuning editor to game owners and runs its jobs. Off unless an admin turns it on.',
  GameRunVerificationEnabled:
    'Runs the game a chat request produced before its version is created, and hands a failure to start or run ' +
    'back to the agents to fix, up to five times. Off: the game is not run and its versions record no verdict.',
  WorkerUnitsEnabled:
    'Lets the unit manager act, by the policy on the Units page. Before Off, set Warm units to 0 and wait until ' +
    'no unit is kept warm. Off: running units end by their own rules.',
};

const OnOffSettings = new Set(['TuningEditorEnabled', 'GameRunVerificationEnabled', 'WorkerUnitsEnabled']);

const settingLabel = (key: string) =>
  key.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2').replace(/([a-z0-9])([A-Z])/g, '$1 $2');

export default function ServerSettings() {
  const [settings, setSettings] = useState<ServerSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getServerSettings()
      .then((list) => {
        setSettings(list.filter((setting) => !UnitsPageKeys.has(setting.key)));
        setError(null);
      })
      .catch((err) => setError(errorMessage(err, 'Could not load the server settings.')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const onSaved = (saved: ServerSetting) =>
    setSettings((list) => list.map((s) => (s.key === saved.key ? saved : s)));

  return (
    <AdminPage
      active="admin-settings"
      eyebrow="Server"
      title="Server settings"
      intro={
        'Settings this environment’s server reads while it runs. A change applies to the next request and the next ' +
        'pipeline job, with no deploy or restart. The worker units’ policy is on the Units page.'
      }
    >
      {error && (
        <div
          style={{
            padding: '10px 14px',
            border: '1px solid var(--danger-line)',
            background: 'var(--danger-wash)',
            color: 'var(--danger)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="muted">Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {settings.map((setting) => (
            <SettingRow
              key={setting.key}
              setting={setting}
              label={settingLabel(setting.key)}
              description={DESCRIPTIONS[setting.key]}
              onOff={OnOffSettings.has(setting.key)}
              onSaved={onSaved}
            />
          ))}
        </div>
      )}
    </AdminPage>
  );
}
